import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { upsertBusinessSummary } from 'lib/api/business';
import { UpsertBody } from 'lib/api/business/types';
import CustomButton from 'lib/designSystem/Button';
import { BusinessSummary } from 'lib/types/models';
import { regionsHierarchy } from 'pages/BusinessProfile/Profile/components/Client/components/newLocationConstants';
import React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

interface AddressEditorProps {
  identifier: string | null;
  summary: BusinessSummary | null;
  onCancel: () => void;
}

const AddressEditor: React.FC<AddressEditorProps> = ({
  identifier,
  summary,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = upsertBusinessSummary(dispatch, identifier);
  const [form, setForm] = useState<UpsertBody>({
    email: summary?.email ?? null,
    region: summary?.region ?? null,
    province: summary?.province ?? null,
    commune: summary?.commune ?? null,
    address: summary?.address ?? null,
    economicSector: summary?.economicSector ?? null,
    economicSubSector: summary?.economicSubSector ?? null,
    economicActivity: summary?.economicActivity ?? null,
    economicActivityCode: summary?.economicActivityCode ?? null,
    salesSegment: summary?.salesSegment ?? null,
    uaf: summary?.uaf ? new Date(summary.uaf) : null,
    observations: summary?.observations ?? null,
  });

  const selectedRegion = regionsHierarchy.find((r) => r.name === form.region);
  const provinces = selectedRegion?.provinces ?? [];
  const selectedProvince = provinces.find((p) => p.name === form.province);
  const communes = selectedProvince?.communes ?? [];

  const handleSubmit = () => {
    mutate(form, {
      onSuccess: () => {
        onCancel();
      },
    });
  };

  const handleSelectChange =
    (field: keyof UpsertBody) => (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        if (field === 'region') {
          next.province = '';
          next.commune = '';
        } else if (field === 'province') {
          next.commune = '';
        }
        return next;
      });
    };

  const handleInputChange =
    (field: keyof UpsertBody) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  return (
    <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Región</InputLabel>
            <Select
              value={form.region ?? ''}
              onChange={handleSelectChange('region')}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              label="Región"
            >
              {regionsHierarchy.map((region) => (
                <MenuItem key={region.id} value={region.name}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Provincia</InputLabel>
            <Select
              value={form.province ?? ''}
              onChange={handleSelectChange('province')}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              label="Provincia"
              disabled={!form.region}
            >
              {provinces.map((province) => (
                <MenuItem key={province.id} value={province.name}>
                  {province.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Comuna</InputLabel>
            <Select
              value={form.commune ?? ''}
              onChange={handleSelectChange('commune')}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              label="Comuna"
              disabled={!form.province}
            >
              {communes.map((commune, index) => (
                <MenuItem key={`${commune.id}-${index}`} value={commune.name}>
                  {commune.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            size="small"
            label="Dirección"
            value={form.address ?? ''}
            onChange={handleInputChange('address')}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'end', gap: 1, mt: 1 }}>
        <CustomButton size="small" color="secondary" onClick={onCancel}>
          Cancelar
        </CustomButton>
        <CustomButton
          size="small"
          color="primary"
          onClick={handleSubmit}
          disabled={isPending}
          loading={isPending}
        >
          Guardar
        </CustomButton>
      </Box>
    </Box>
  );
};

export default AddressEditor;
